import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Check, Loader2, UserCircle2 } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { announceProfileUpdated } from "@/components/admin/SidebarProfileFooter";

const BASE = process.env.REACT_APP_BACKEND_URL;
const TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_BYTES = 8 * 1024 * 1024;

export const SavedTick = ({ show, testId }) => (show ? (
  <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-emerald-500 normal-case tracking-normal" data-testid={testId}>
    <Check className="h-3 w-3"/>Saved
  </span>
) : null);

// v2.8+E350 · Own-profile photo upload inside the profile editors; saves on pick.
export const ProfilePhotoUploader = ({ testIdPrefix = "profile-photo" }) => {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/admin/me/profile");
      setUrl(data.profile_image_url || "");
    } catch { /* card keeps initials */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onFile = async (file) => {
    if (!TYPES.includes(file.type)) { toast.error("Use PNG, JPEG, or WebP."); return; }
    if (file.size > MAX_BYTES) { toast.error("File too large. Max 8 MB."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminApi.post("/admin/me/profile/avatar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUrl(data.profile_image_url || "");
      setSaved(true);
      announceProfileUpdated();
      toast.success("Profile picture updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Photo upload failed");
    } finally { setBusy(false); }
  };

  const src = url ? (/^https?:\/\//i.test(url) ? url : `${BASE}${url}`) : "";
  return (
    <div className="flex items-center gap-3" data-testid={testIdPrefix}>
      <div className="h-12 w-12 rounded-full overflow-hidden border-2 border-amber-400/40 bg-zinc-800 grid place-items-center flex-shrink-0">
        {src
          ? <img src={src} alt="Profile" className="h-full w-full object-cover" data-testid={`${testIdPrefix}-img`}/>
          : <UserCircle2 className="h-6 w-6 text-amber-300"/>}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept={TYPES.join(",")}
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ""; }}
        data-testid={`${testIdPrefix}-input`}
      />
      <Button
        type="button"
        size="sm"
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold"
        data-testid={`${testIdPrefix}-upload-btn`}
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <><Camera className="h-3.5 w-3.5 mr-1"/>Upload Photo</>}
      </Button>
      <SavedTick show={saved} testId={`${testIdPrefix}-saved`}/>
    </div>
  );
};
